import React, { useEffect, useState } from "react";
import { Sparkles, Upload, AlertTriangle, ArrowRight, X, Loader2 } from "lucide-react";
import { EtapasIA, iaImportService } from "../../services/iaImportService";
import { itemEmpreendimentoService } from "../../services/empreendimentoService";
import { levenshteinDistance } from "../../utils/levenshtein";
import { ImportacaoIAResultado, UnidadeIAExtraida } from "../../types/iaImport";

// Sem limite rígido no backend, mas books de verdade testados (~40MB) levam
// bem mais tempo pra fazer upload + extrair — acima disso a chance de dar
// timeout ou de ser um arquivo enviado por engano é grande. Barrar aqui evita
// o usuário esperar minutos só pra descobrir isso depois.
const TAMANHO_MAXIMO_MB = 40;
const TAMANHO_MAXIMO_BYTES = TAMANHO_MAXIMO_MB * 1024 * 1024;

const NOMES_ETAPA: Record<keyof EtapasIA, string> = {
  book: "book",
  tabela: "tabela de preço",
};

// --- Estilos reaproveitados do resto do app ---
const inputStyle =
  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500";
const labelStyle = "block text-sm font-medium text-gray-700 mb-1";

const tiposDeEmpreendimento: Record<number, string> = {
  1: "Lote",
  2: "Apartamento",
  3: "Comercial",
  4: "Casa",
  5: "Compacto",
};

const disponibilidadeOptions = [
  { value: 1, label: "Disponível" },
  { value: 2, label: "Vendido" },
  { value: 3, label: "Distratado" },
  { value: 4, label: "Bloqueada" },
];

// Linha de unidade em edição na tela de revisão. `incluir` deixa desmarcar
// uma linha sem precisar apagar os dados (útil se a IA leu uma linha que na
// verdade não é uma unidade de verdade, ex.: um total/subtotal da tabela).
interface UnidadeRascunho extends UnidadeIAExtraida {
  incluir: boolean;
}

// Um "campo em dúvida" recebe destaque visual — não é feito pra esconder
// nada, é o oposto: chamar atenção pro que a própria IA disse que não tem
// certeza, em vez de fazer a pessoa reconferir o documento inteiro.
const CampoComAviso: React.FC<{ emDuvida: boolean; children: React.ReactNode }> = ({
  emDuvida,
  children,
}) => (
  <div className={emDuvida ? "ring-2 ring-amber-400 rounded-md" : ""}>{children}</div>
);

interface ImportarEmpreendimentoIAProps {
  /** Chamado quando o usuário confirma os dados revisados e quer seguir pro
   * formulário completo (etapa 1) já pré-preenchido. */
  onConcluir: (dados: {
    basicData: Record<string, any>;
    specificData: any;
    enderecoTexto: { uf?: string; cidade?: string; bairro?: string };
    incorporadoraTexto: string;
  }) => void;
  /** Chamado quando o usuário prefere preencher tudo manualmente. */
  onPular: () => void;
}

const ImportarEmpreendimentoIA: React.FC<ImportarEmpreendimentoIAProps> = ({
  onConcluir,
  onPular,
}) => {
  const [bookFile, setBookFile] = useState<File | null>(null);
  const [tabelaFile, setTabelaFile] = useState<File | null>(null);
  const [analisando, setAnalisando] = useState(false);
  const [segundosAnalisando, setSegundosAnalisando] = useState(0);
  const [etapas, setEtapas] = useState<EtapasIA>({});
  const [erro, setErro] = useState<string | null>(null);

  const [resultado, setResultado] = useState<ImportacaoIAResultado | null>(null);

  // --- Rascunho editável do empreendimento (fase de revisão) ---
  const [nome, setNome] = useState("");
  const [incorporadoraTexto, setIncorporadoraTexto] = useState("");
  const [tipoEmpreendimento, setTipoEmpreendimento] = useState<number[]>([]);
  const [isLancamento, setIsLancamento] = useState(false);
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [uf, setUf] = useState("");
  const [areaTotal, setAreaTotal] = useState<string>("");
  const [areaConstruida, setAreaConstruida] = useState<string>("");
  const [dataEntrega, setDataEntrega] = useState("");
  const [dataInicioComercializacao, setDataInicioComercializacao] = useState("");
  const [observacao, setObservacao] = useState("");
  const [itens, setItens] = useState<string[]>([]);
  const [camposNaoEncontrados, setCamposNaoEncontrados] = useState<string[]>([]);

  const [unidades, setUnidades] = useState<UnidadeRascunho[]>([]);
  const [linhasComDuvida, setLinhasComDuvida] = useState<string[]>([]);

  // Torres "declaradas" pra tela de revisão — semeadas a partir dos
  // torre_texto que a IA já identificou, mas o usuário pode criar mais (ex.:
  // a IA só achou 1 torre e ele sabe que são 3) e renomear cada uma de uma
  // vez só, sem precisar editar linha por linha.
  const [torresDisponiveis, setTorresDisponiveis] = useState<string[]>([]);
  const [qtdTorresNovas, setQtdTorresNovas] = useState("");
  const [torreEditandoOriginal, setTorreEditandoOriginal] = useState<string | null>(null);

  // Se a página foi recarregada no meio de uma análise, o job continua
  // rodando no backend (até 20min de TTL) — só perdemos o acompanhamento
  // dele aqui. Retoma o polling automaticamente em vez de deixar o usuário
  // achar que precisa enviar tudo de novo (e pagar outra chamada à IA).
  useEffect(() => {
    const jobEmAndamento = iaImportService.recuperarJobEmAndamento();
    if (!jobEmAndamento) return;

    setErro(null);
    setAnalisando(true);
    setSegundosAnalisando(0);
    iaImportService
      .acompanharJob(jobEmAndamento, (etapasAtuais) => {
        setEtapas(etapasAtuais);
        setSegundosAnalisando((s) => s + 3);
      })
      .then(aplicarResultado)
      .catch((err) => {
        iaImportService.esquecerJobEmAndamento();
        tratarErro(err);
      })
      .finally(() => setAnalisando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validarTamanho = (file: File): boolean => {
    if (file.size > TAMANHO_MAXIMO_BYTES) {
      setErro(
        `"${file.name}" tem ${(file.size / 1024 / 1024).toFixed(1)}MB — o limite é ${TAMANHO_MAXIMO_MB}MB. Envie um arquivo menor.`
      );
      return false;
    }
    return true;
  };

  const handleSelecionarBook = (file: File | null) => {
    if (file && !validarTamanho(file)) return;
    setErro(null);
    setBookFile(file);
  };

  const handleSelecionarTabela = (file: File | null) => {
    if (file && !validarTamanho(file)) return;
    setErro(null);
    setTabelaFile(file);
  };

  const aplicarResultado = (res: ImportacaoIAResultado) => {
    setResultado(res);

    if (res.empreendimento) {
        const e = res.empreendimento;
        // Helper pra normalizar formato de data (IA costuma retornar DD/MM/YYYY ou similar)
        const normalizarData = (dataTexto: string): string => {
          if (!dataTexto) return "";
          // Se já está em YYYY-MM-DD, retorna como está
          if (/^\d{4}-\d{2}-\d{2}$/.test(dataTexto)) return dataTexto;
          // Se está em DD/MM/YYYY, converte
          if (/^\d{2}\/\d{2}\/\d{4}$/.test(dataTexto)) {
            const [dia, mes, ano] = dataTexto.split('/');
            return `${ano}-${mes}-${dia}`;
          }
          // Se está em DD-MM-YYYY, converte
          if (/^\d{2}-\d{2}-\d{4}$/.test(dataTexto)) {
            const [dia, mes, ano] = dataTexto.split('-');
            return `${ano}-${mes}-${dia}`;
          }
          // Caso não reconheça, retorna como está
          return dataTexto;
        };

        setNome(e.nome || "");
        setIncorporadoraTexto(e.incorporadora_texto || "");
        setTipoEmpreendimento(e.tipo_empreendimento || []);
        setIsLancamento(e.is_lancamento);
        setBairro(e.bairro || "");
        setCidade(e.cidade || "");
        setUf(e.uf || "");
        setAreaTotal(e.area_total != null ? String(e.area_total) : "");
        setAreaConstruida(e.area_construida != null ? String(e.area_construida) : "");
        setDataEntrega(normalizarData(e.data_entrega || ""));
        setDataInicioComercializacao(normalizarData(e.data_inicio_comercializacao || ""));
        setObservacao(e.observacao || "");
        setItens(e.itens || []);
        setCamposNaoEncontrados(e.campos_nao_encontrados || []);
      }

      if (res.tabela) {
        if (!res.empreendimento && res.tabela.nome_empreendimento) {
          setNome(res.tabela.nome_empreendimento);
          setIncorporadoraTexto(res.tabela.incorporadora_texto || "");
        }
        setUnidades(res.tabela.unidades.map((u) => ({ ...u, incluir: true })));
        setLinhasComDuvida(res.tabela.linhas_com_duvida || []);
        setTorresDisponiveis(
          Array.from(new Set(res.tabela.unidades.map((u) => u.torre_texto).filter((t): t is string => !!t)))
        );
      }
  };

  const tratarErro = (err: any) => {
    console.error("Erro ao importar via IA:", err);
    const msg =
      err.response?.status === 503
        ? "Importação via IA está indisponível no momento (verifique a configuração da chave, ou tente novamente em instantes)."
        : err.response?.status === 429
        ? "Limite de importações por hora atingido. Tente novamente mais tarde."
        : err.response?.data?.errors?.ia?.[0] ||
          err.message ||
          "Erro ao analisar os documentos. Tente novamente.";
    setErro(msg);
  };

  const handleAnalisar = async () => {
    if (!bookFile && !tabelaFile) {
      setErro("Selecione ao menos um arquivo: book ou tabela de preço.");
      return;
    }
    setErro(null);
    setAnalisando(true);
    setSegundosAnalisando(0);
    setEtapas({});
    try {
      const res = await iaImportService.importar(
        bookFile || undefined,
        tabelaFile || undefined,
        (etapasAtuais) => {
          setEtapas(etapasAtuais);
          setSegundosAnalisando((s) => s + 3);
        }
      );
      aplicarResultado(res);
    } catch (err: any) {
      tratarErro(err);
    } finally {
      setAnalisando(false);
    }
  };

  const toggleTipo = (valor: number) => {
    setTipoEmpreendimento((prev) =>
      prev.includes(valor) ? prev.filter((v) => v !== valor) : [...prev, valor]
    );
  };

  const removerItem = (item: string) => setItens((prev) => prev.filter((i) => i !== item));

  const atualizarUnidade = (index: number, campo: keyof UnidadeRascunho, valor: any) => {
    setUnidades((prev) => prev.map((u, i) => (i === index ? { ...u, [campo]: valor } : u)));
  };

  // Cria N torres vazias ("Torre X", evitando colidir com nomes já usados)
  // pra atribuir unidades depois — útil quando a IA não achou torre nenhuma
  // (ou achou menos do que existe de verdade) e o usuário quer declarar a
  // estrutura manualmente antes de organizar as linhas.
  const criarTorresAutomaticamente = () => {
    const qtd = parseInt(qtdTorresNovas, 10);
    if (!qtd || qtd <= 0) return;
    setTorresDisponiveis((prev) => {
      const nomesExistentes = new Set(prev);
      const novas: string[] = [];
      let contador = prev.length + 1;
      while (novas.length < qtd) {
        const candidato = `Torre ${contador}`;
        if (!nomesExistentes.has(candidato)) {
          novas.push(candidato);
          nomesExistentes.add(candidato);
        }
        contador++;
      }
      return [...prev, ...novas];
    });
    setQtdTorresNovas("");
  };

  // Renomear uma torre já declarada atualiza o rótulo E todas as unidades
  // que já estavam atribuídas a ela — editar célula por célula pra 40
  // unidades da mesma torre seria inviável.
  const renomearTorre = (indice: number, novoValor: string) => {
    setTorresDisponiveis((prev) => prev.map((t, i) => (i === indice ? novoValor : t)));
  };

  const confirmarRenomeioTorre = (novoValor: string) => {
    const nomeAntigo = torreEditandoOriginal;
    const novo = novoValor.trim();
    if (nomeAntigo && novo && nomeAntigo !== novo) {
      setUnidades((prev) =>
        prev.map((u) => (u.torre_texto === nomeAntigo ? { ...u, torre_texto: novo } : u))
      );
    }
    setTorreEditandoOriginal(null);
  };

  const removerTorreDisponivel = (indice: number) => {
    // Só some da lista de opções rápidas — não mexe no torre_texto de quem já
    // estava atribuído a ela (evita apagar dado sem querer).
    setTorresDisponiveis((prev) => prev.filter((_, i) => i !== indice));
  };

  /**
   * Monta a base de uma unidade pro payload do formulário (campos comuns a
   * torre e avulsa — só torre/andar/numero_apartamento variam por posição).
   */
  const construirParteBase = (u: UnidadeRascunho) => ({
    nome: u.nome,
    area_privativa_m2: u.area_privativa_m2 != null ? String(u.area_privativa_m2) : "",
    valor_imovel: u.valor_imovel != null ? String(u.valor_imovel) : "",
    valor_avaliacao: u.valor_avaliacao != null ? String(u.valor_avaliacao) : "",
    forma_pagamento: u.forma_pagamento || "",
    disponibilidade: u.disponibilidade,
    quantidade_garagem: u.quantidade_garagem ?? "",
    tipo_parte_empreendimento: [] as (string | number)[],
    atributos_extras: {} as Record<string, any>,
    mcmv: false,
    tipo_produto: u.tipo_produto ?? 2,
    vagas_rotativas: "" as number | "",
    pavimento_texto: u.pavimento_texto || "",
    confirmada: !u.duvida,
  });

  /**
   * Agrupa as unidades revisadas em torres de verdade (Torre.unidades como
   * grade andar x posição) e só deixa cair em "avulsas" o que não tiver
   * torre_texto + andar preenchidos ao mesmo tempo — a IA só deixa os dois em
   * branco quando a unidade genuinamente não se encaixa no padrão do resto
   * da tabela (ver prompt no backend). Sem isso, tudo caía em avulsas mesmo
   * quando havia uma torre clara.
   */
  const agruparEmTorres = (lista: UnidadeRascunho[]) => {
    const comTorre = lista.filter((u) => u.torre_texto && u.andar != null);
    const semTorre = lista.filter((u) => !(u.torre_texto && u.andar != null));

    const gruposPorTorre = new Map<string, UnidadeRascunho[]>();
    comTorre.forEach((u) => {
      const chave = u.torre_texto!;
      if (!gruposPorTorre.has(chave)) gruposPorTorre.set(chave, []);
      gruposPorTorre.get(chave)!.push(u);
    });

    const torres = Array.from(gruposPorTorre.entries()).map(([torreTexto, unidadesDaTorre], torreIndex) => {
      // Andar mais alto primeiro — é assim que a grade (TorreView) já espera
      // as linhas (linha 0 = andar mais alto).
      const andaresDistintos = Array.from(new Set(unidadesDaTorre.map((u) => u.andar!))).sort(
        (a, b) => b - a
      );
      const unidadesGrade = andaresDistintos.map((andarNum) =>
        unidadesDaTorre
          .filter((u) => u.andar === andarNum)
          .map((u, colIndex) => ({
            ...construirParteBase(u),
            torre: torreIndex + 1,
            andar: andarNum,
            numero_apartamento: colIndex + 1,
          }))
      );
      const nomeTorre = /torre/i.test(torreTexto) ? torreTexto : `Torre ${torreTexto}`;
      return { nome: nomeTorre, unidades: unidadesGrade };
    });

    const avulsas = semTorre.map((u) => ({
      ...construirParteBase(u),
      torre: 0,
      andar: 0,
      numero_apartamento: 0,
    }));

    return { torres, avulsas };
  };

  const handleContinuar = async () => {
    // Resolve os itens de texto contra os já cadastrados (fuzzy match) só na
    // hora de seguir — não precisa bloquear a tela de revisão esperando essa
    // chamada.
    let itemIds: string[] = [];
    try {
      const todosItens = await itemEmpreendimentoService.getAllItens();
      itemIds = itens
        .map((textoItem) => {
          const melhor = todosItens
            .map((it) => ({ it, distance: levenshteinDistance(textoItem, it.name) }))
            .sort((a, b) => a.distance - b.distance)[0];
          return melhor && melhor.distance <= textoItem.length * 0.4 ? melhor.it.id : null;
        })
        .filter((id): id is string => id !== null);
    } catch (err) {
      console.error("Erro ao resolver itens:", err);
    }

    const basicData = {
      nome,
      tipo_empreendimento: tipoEmpreendimento,
      is_lancamento: isLancamento,
      area_total: areaTotal ? Number(areaTotal) : 0,
      area_construida: areaConstruida ? Number(areaConstruida) : 0,
      data_entrega: dataEntrega || undefined,
      data_inicio_comercializacao: dataInicioComercializacao || undefined,
      observacao,
    };

    const { torres, avulsas } = agruparEmTorres(unidades.filter((u) => u.incluir));

    const specificData = {
      tipo_empreendimento: tipoEmpreendimento,
      item_empreendimento: itemIds,
      torres,
      unidades_sem_torre: avulsas,
    };

    onConcluir({
      basicData,
      specificData,
      enderecoTexto: { uf, cidade, bairro },
      incorporadoraTexto,
    });
  };

  const temResultado = resultado !== null;

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Sparkles className="text-indigo-600" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Importar com IA</h2>
            <p className="text-sm text-gray-500">
              Envie o book e/ou a tabela de preço — a IA lê e pré-preenche o cadastro. Você revisa
              tudo aqui antes de continuar; nada é salvo sem sua confirmação.
            </p>
          </div>
        </div>
        {!temResultado && (
          <button
            onClick={onPular}
            className="flex-shrink-0 text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Pular e preencher manualmente
          </button>
        )}
      </div>

      {erro && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex justify-between items-start">
          <span>{erro}</span>
          <button onClick={() => setErro(null)}>
            <X size={18} />
          </button>
        </div>
      )}

      {!temResultado && (
        <div className="bg-white shadow-md rounded-lg p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelStyle}>Book (marketing/apresentação)</label>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                onChange={(e) => handleSelecionarBook(e.target.files?.[0] || null)}
                className={inputStyle}
              />
              {bookFile && <p className="mt-1 text-xs text-gray-600">{bookFile.name}</p>}
            </div>
            <div>
              <label className={labelStyle}>Tabela de preço</label>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                onChange={(e) => handleSelecionarTabela(e.target.files?.[0] || null)}
                className={inputStyle}
              />
              {tabelaFile && <p className="mt-1 text-xs text-gray-600">{tabelaFile.name}</p>}
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Pode enviar só um dos dois. PDF, PNG, JPEG ou WEBP, até {TAMANHO_MAXIMO_MB}MB. Cada
            análise pode levar de 20s a 1 minuto.
          </p>
          <button
            onClick={handleAnalisar}
            disabled={analisando || (!bookFile && !tabelaFile)}
            className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {analisando ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                {(Object.keys(etapas) as (keyof EtapasIA)[]).length > 0
                  ? (Object.keys(etapas) as (keyof EtapasIA)[])
                      .map(
                        (chave) =>
                          `${NOMES_ETAPA[chave]}: ${
                            etapas[chave] === "concluido" ? "pronto" : "analisando"
                          }`
                      )
                      .join(" · ")
                  : "Analisando com IA..."}
                {segundosAnalisando > 0 && ` (${segundosAnalisando}s)`}
              </>
            ) : (
              <>
                <Upload size={18} /> Analisar com IA
              </>
            )}
          </button>
        </div>
      )}

      {temResultado && (
        <div className="space-y-6">
          {camposNaoEncontrados.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 rounded-md flex items-start gap-2 text-sm">
              <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
              <div>
                <strong>A IA não encontrou (ou não teve certeza) destes campos:</strong>{" "}
                {camposNaoEncontrados.join(", ")}. Preencha manualmente abaixo.
              </div>
            </div>
          )}

          <div className="bg-white shadow-md rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Dados do empreendimento</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CampoComAviso emDuvida={camposNaoEncontrados.includes("nome_empreendimento")}>
                <label className={labelStyle}>Nome</label>
                <input value={nome} onChange={(e) => setNome(e.target.value)} className={inputStyle} />
              </CampoComAviso>
              <div>
                <label className={labelStyle}>Incorporadora / Construtora</label>
                <input
                  value={incorporadoraTexto}
                  onChange={(e) => setIncorporadoraTexto(e.target.value)}
                  className={inputStyle}
                  placeholder="Como aparece no documento"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Na próxima tela tentamos casar automaticamente com uma incorporadora já
                  cadastrada por esse nome.
                </p>
              </div>
            </div>

            <div>
              <label className={labelStyle}>Tipo de empreendimento</label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(tiposDeEmpreendimento).map(([valor, label]) => (
                  <button
                    key={valor}
                    type="button"
                    onClick={() => toggleTipo(Number(valor))}
                    className={`px-3 py-1 rounded-full text-sm border ${
                      tipoEmpreendimento.includes(Number(valor))
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-gray-700 border-gray-300"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={isLancamento}
                onChange={(e) => setIsLancamento(e.target.checked)}
              />
              É lançamento
            </label>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <CampoComAviso emDuvida={!uf}>
                <label className={labelStyle}>UF</label>
                <input value={uf} onChange={(e) => setUf(e.target.value.toUpperCase())} className={inputStyle} maxLength={2} />
              </CampoComAviso>
              <CampoComAviso emDuvida={!cidade}>
                <label className={labelStyle}>Cidade</label>
                <input value={cidade} onChange={(e) => setCidade(e.target.value)} className={inputStyle} />
              </CampoComAviso>
              <CampoComAviso emDuvida={!bairro}>
                <label className={labelStyle}>Bairro</label>
                <input value={bairro} onChange={(e) => setBairro(e.target.value)} className={inputStyle} />
              </CampoComAviso>
            </div>
            <p className="text-xs text-gray-500 -mt-2">
              Localização em texto livre — na próxima tela tentamos casar com município/bairro já
              cadastrados. O endereço completo (logradouro/número) você confirma lá.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CampoComAviso emDuvida={camposNaoEncontrados.includes("area_total_m2")}>
                <label className={labelStyle}>Área total (m²)</label>
                <input type="number" value={areaTotal} onChange={(e) => setAreaTotal(e.target.value)} className={inputStyle} />
              </CampoComAviso>
              <CampoComAviso emDuvida={camposNaoEncontrados.includes("area_construida_m2")}>
                <label className={labelStyle}>Área construída (m²)</label>
                <input type="number" value={areaConstruida} onChange={(e) => setAreaConstruida(e.target.value)} className={inputStyle} />
              </CampoComAviso>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelStyle}>Data de entrega</label>
                <input value={dataEntrega} onChange={(e) => setDataEntrega(e.target.value)} className={inputStyle} placeholder="como aparece no documento" />
              </div>
              <CampoComAviso emDuvida={camposNaoEncontrados.includes("data_inicio_comercializacao")}>
                <label className={labelStyle}>Início da comercialização</label>
                <input
                  value={dataInicioComercializacao}
                  onChange={(e) => setDataInicioComercializacao(e.target.value)}
                  className={inputStyle}
                  placeholder="como aparece no documento"
                />
              </CampoComAviso>
            </div>

            <div>
              <label className={labelStyle}>Observação</label>
              <textarea
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                rows={3}
                className={inputStyle}
              />
            </div>

            <div>
              <label className={labelStyle}>Itens (lazer/segurança/infraestrutura)</label>
              <div className="flex flex-wrap gap-2">
                {itens.length === 0 && (
                  <span className="text-sm text-gray-400">Nenhum item identificado.</span>
                )}
                {itens.map((item) => (
                  <span
                    key={item}
                    className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                  >
                    {item}
                    <button onClick={() => removerItem(item)} className="text-gray-400 hover:text-red-500">
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Tentamos casar cada um com os itens já cadastrados ao continuar. O que não achar
                fica de fora — adicione manualmente na tela seguinte se precisar.
              </p>
            </div>
          </div>

          {unidades.length > 0 && (() => {
            const incluidas = unidades.filter((u) => u.incluir);
            const { torres: torresPreview, avulsas: avulsasPreview } = agruparEmTorres(incluidas);
            const qtdGeradas = unidades.filter((u) => u.gerada).length;
            return (
            <div className="bg-white shadow-md rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-1">
                Unidades ({incluidas.length} de {unidades.length} incluídas)
              </h3>
              {qtdGeradas > 0 && (
                <p className="text-sm text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-3 py-2 mb-2">
                  {qtdGeradas} unidade(s) foram <strong>geradas a partir do padrão descrito no book</strong>{" "}
                  (ex.: "N andares, M unidades por andar") — o book não listava cada unidade
                  individualmente. Confira com atenção redobrada antes de continuar.
                </p>
              )}
              <p className="text-sm text-gray-500 mb-1">
                {torresPreview.length > 0 ? (
                  <>
                    Vão para{" "}
                    <strong>
                      {torresPreview.length} torre{torresPreview.length > 1 ? "s" : ""}
                    </strong>
                    {torresPreview.map((t) => ` "${t.nome}"`).join(", ")}
                    {avulsasPreview.length > 0 && (
                      <> + {avulsasPreview.length} unidade(s) avulsa(s)</>
                    )}
                    .
                  </>
                ) : (
                  <>Nenhuma torre identificada — todas entram como unidades avulsas.</>
                )}
                {linhasComDuvida.length > 0 && (
                  <>
                    {" "}
                    <span className="text-amber-700 font-medium">
                      {linhasComDuvida.length} linha(s) marcada(s) com dúvida pela IA — destacadas
                      abaixo.
                    </span>
                  </>
                )}
              </p>
              <p className="text-xs text-gray-500 mb-4">
                Torre e Andar em branco = a IA não identificou o padrão pra essa linha e ela vai
                como avulsa. Preencha manualmente aqui se souber onde ela entra, ou deixe assim e
                organize depois pelo recurso de arrastar unidades.
              </p>

              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Torres identificadas</h4>
                <div className="flex flex-wrap gap-2 mb-3">
                  {torresDisponiveis.length === 0 && (
                    <span className="text-sm text-gray-400">Nenhuma torre ainda.</span>
                  )}
                  {torresDisponiveis.map((nomeTorre, indice) => (
                    <div
                      key={indice}
                      className="flex items-center gap-1 bg-white border border-gray-300 rounded-md pl-2 pr-1 py-1"
                    >
                      <input
                        value={nomeTorre}
                        onFocus={() => setTorreEditandoOriginal(nomeTorre)}
                        onChange={(e) => renomearTorre(indice, e.target.value)}
                        onBlur={(e) => confirmarRenomeioTorre(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                        }}
                        title="Renomear — atualiza todas as unidades já atribuídas a essa torre"
                        className="text-sm border-none focus:outline-none focus:ring-1 focus:ring-indigo-400 rounded px-1 w-28"
                      />
                      <button
                        type="button"
                        onClick={() => removerTorreDisponivel(indice)}
                        title="Remover da lista (não mexe nas unidades já atribuídas)"
                        className="text-gray-400 hover:text-red-500"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-end gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nº de torres a criar</label>
                    <input
                      type="number"
                      min={1}
                      value={qtdTorresNovas}
                      onChange={(e) => setQtdTorresNovas(e.target.value)}
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="Ex: 3"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={criarTorresAutomaticamente}
                    disabled={!qtdTorresNovas}
                    className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-semibold rounded-md hover:bg-indigo-700 disabled:bg-gray-300"
                  >
                    Criar torres automaticamente
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Cria torres vazias ("Torre 1", "Torre 2"...) pra você escolher na coluna Torre da
                  tabela abaixo. Renomeie clicando no nome de uma torre já criada — vale pra todas
                  as unidades já atribuídas a ela.
                </p>
              </div>

              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2"></th>
                      <th className="p-2 text-left">Nome</th>
                      <th className="p-2 text-left">Torre</th>
                      <th className="p-2 text-left">Andar</th>
                      <th className="p-2 text-left">Pavimento</th>
                      <th className="p-2 text-left">Área (m²)</th>
                      <th className="p-2 text-left">Valor (R$)</th>
                      <th className="p-2 text-left">Avaliação (R$)</th>
                      <th className="p-2 text-left">Disponibilidade</th>
                      <th className="p-2 text-left">Garagens</th>
                      <th className="p-2 text-left">Forma de pagamento</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {unidades.map((u, i) => (
                      <tr key={i} className={u.duvida ? "bg-amber-50" : !u.incluir ? "bg-gray-50 opacity-50" : ""}>
                        <td className="p-2 text-center">
                          <input
                            type="checkbox"
                            checked={u.incluir}
                            onChange={(e) => atualizarUnidade(i, "incluir", e.target.checked)}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            value={u.nome}
                            onChange={(e) => atualizarUnidade(i, "nome", e.target.value)}
                            className="w-40 px-2 py-1 border border-gray-300 rounded"
                          />
                          {u.gerada && (
                            <span
                              className="block mt-1 text-[10px] uppercase tracking-wide font-semibold text-indigo-600"
                              title="Gerada a partir do padrão descrito no book, não lida de uma linha explícita"
                            >
                              Gerada
                            </span>
                          )}
                        </td>
                        <td className="p-2">
                          <select
                            value={u.torre_texto ?? ""}
                            onChange={(e) => atualizarUnidade(i, "torre_texto", e.target.value || null)}
                            title="Deixe em '—' pra unidade avulsa. Crie/renomeie torres no painel acima."
                            className="w-28 px-2 py-1 border border-gray-300 rounded"
                          >
                            <option value="">—</option>
                            {/* Garante que o valor atual sempre apareça, mesmo se ainda não
                                estiver na lista de torres declaradas (ex.: veio direto da IA
                                antes do usuário mexer no painel de torres). */}
                            {u.torre_texto && !torresDisponiveis.includes(u.torre_texto) && (
                              <option value={u.torre_texto}>{u.torre_texto}</option>
                            )}
                            {torresDisponiveis.map((nomeTorre) => (
                              <option key={nomeTorre} value={nomeTorre}>
                                {nomeTorre}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-2">
                          <input
                            type="number"
                            value={u.andar ?? ""}
                            onChange={(e) => atualizarUnidade(i, "andar", e.target.value !== "" ? Number(e.target.value) : null)}
                            placeholder="—"
                            title="Térreo = 0. Deixe em branco pra unidade avulsa"
                            className="w-16 px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="p-2">
                          <input
                            value={u.pavimento_texto ?? ""}
                            onChange={(e) => atualizarUnidade(i, "pavimento_texto", e.target.value || null)}
                            placeholder="Ex: Cobertura"
                            title="Rótulo do pavimento, se quiser sobrepor o número do andar (opcional)"
                            className="w-28 px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="number"
                            value={u.area_privativa_m2 ?? ""}
                            onChange={(e) => atualizarUnidade(i, "area_privativa_m2", e.target.value ? Number(e.target.value) : null)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="number"
                            value={u.valor_imovel ?? ""}
                            onChange={(e) => atualizarUnidade(i, "valor_imovel", e.target.value ? Number(e.target.value) : null)}
                            className="w-32 px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="number"
                            value={u.valor_avaliacao ?? ""}
                            onChange={(e) => atualizarUnidade(i, "valor_avaliacao", e.target.value ? Number(e.target.value) : null)}
                            className="w-32 px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="p-2">
                          <select
                            value={u.disponibilidade}
                            onChange={(e) => atualizarUnidade(i, "disponibilidade", Number(e.target.value))}
                            className="px-2 py-1 border border-gray-300 rounded"
                          >
                            {disponibilidadeOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-2">
                          <input
                            type="number"
                            value={u.quantidade_garagem ?? ""}
                            onChange={(e) => atualizarUnidade(i, "quantidade_garagem", e.target.value ? Number(e.target.value) : null)}
                            className="w-16 px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="p-2">
                          <input
                            value={u.forma_pagamento}
                            onChange={(e) => atualizarUnidade(i, "forma_pagamento", e.target.value)}
                            className="w-64 px-2 py-1 border border-gray-300 rounded"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })()}

          <div className="flex justify-between items-center">
            <button
              onClick={() => {
                setResultado(null);
                setBookFile(null);
                setTabelaFile(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Analisar outros arquivos
            </button>
            <button
              onClick={handleContinuar}
              disabled={!nome}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-md hover:bg-emerald-700 disabled:bg-gray-300"
            >
              Continuar para o cadastro completo <ArrowRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportarEmpreendimentoIA;
